import React, { useEffect, useMemo } from 'react';
import { Select, InputNumber } from '@bedrock/components';
import cx from 'classnames';
import AmountOrder from '#components/AmountOrder';
import { useControllableValue, useMemoizedFn } from 'ahooks';
import { currencyEnum, financial } from '#utils/number';
import { objectTransenum } from './utils';
import './CurrencyType.less';
import { localPx2rem, stylePx2Rem } from '#utils';
import { isBlank } from '@bedrock/utils';
import { CurrencyEnum } from '#services/api';
import { useChangeRate } from '#hooks/useChangeRate';
import Decimal from 'decimal.js';

type Props = {
  showAmountOrder?: boolean;
  inputStyle?: React.CSSProperties;
  selectStyle?: React.CSSProperties;
  view?: boolean;
  disabled?: boolean;
  selectDisabled?: boolean;
  inputDisabled?: boolean;
  onChange?: (value) => void;
  value?: {
    value: string | number;
    currency: string;
  };
  showCurrency?: boolean;
  currencyColor?: 'yellow' | 'black' | 'red' | 'green';
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  textStyle?: React.CSSProperties;
  currencyStyle?: React.CSSProperties;
  /** 展示币种，只在readonly模式下有效 */
  viewCurrencyType?: CurrencyEnum;
} & Omit<React.ComponentProps<typeof InputNumber>, 'value'>;

const CurrencyType: React.FC<Props> = (props: Props) => {
  const {
    view,
    disabled,
    selectDisabled,
    selectStyle,
    inputDisabled,
    inputStyle,
    value,
    onChange,
    wrapperProps = {},
    showCurrency = true,
    currencyColor = 'yellow',
    showAmountOrder = true,
    textStyle,
    currencyStyle,
    viewCurrencyType,
    ...otherProps
  } = props;
  const [state, setState] = useControllableValue(props, {
    defaultValue: {
      value: '',
      currency: '',
    } as {
      value: string | number;
      currency: string;
    },
  });
  const currencyOptions = objectTransenum(currencyEnum);
  const { changeRateList } = useChangeRate();

  const exchangeRate = useMemo(() => {
    if (viewCurrencyType && state.currency && view) {
      if (state.currency.toLowerCase() === viewCurrencyType.toLowerCase()) {
        return 1;
      }
      const rate = changeRateList.find(
        (item) =>
          item.from_currency?.toLowerCase() === state.currency.toLowerCase() &&
          item.to_currency?.toLowerCase() === viewCurrencyType.toLowerCase()
      )?.exchange_rate;
      if (isBlank(rate)) {
        console.warn(`汇率不存在: ${state.currency} -> ${viewCurrencyType}`);
      }
      return rate;
    }
    return 1;
  }, [viewCurrencyType, state, view, changeRateList]);

  const handleInputChange = useMemoizedFn((value) => {
    const newState = {
      ...state,
      value,
    };
    setState(newState);
  });

  const handleInputBlurs = useMemoizedFn((e) => {
    const inputValue = e.target.value;
    const newState = {
      ...state,
      value: inputValue,
    };
    setState(newState);
  });

  const handleSelectChange = useMemoizedFn((currency) => {
    const newState = {
      ...state,
      currency,
    };
    setState(newState);
  });

  const { className, ...restWrapperProps } = wrapperProps;

  const rateChangeAvailable = useMemo(() => {
    return !isBlank(exchangeRate) && !isNaN(Number(state?.value));
  }, [exchangeRate, state?.value]);

  return (
    <div
      className={cx(
        'sform-item-currency-type',
        {
          'wrapper-view': view,
        },
        currencyColor,
        className
      )}
      {...restWrapperProps}
    >
      <div
        className={cx('currency-type-left', {
          view: view,
        })}
      >
        {view ? (
          <div className={cx('currency-type-text')} style={textStyle}>
            {state?.value || state?.value === 0 ? (
              <span>
                {financial(
                  rateChangeAvailable
                    ? new Decimal(state?.value).mul(exchangeRate).toNumber()
                    : state?.value
                )}
              </span>
            ) : (
              '--'
            )}
          </div>
        ) : (
          <InputNumber
            style={{ ...inputStyle }}
            max={Number.MAX_SAFE_INTEGER - 1}
            min={0.01}
            value={state?.value || ''}
            direction="vertical"
            onChange={handleInputChange}
            disabled={inputDisabled || disabled}
            toFixed={2}
            onBlur={handleInputBlurs}
            autoComplete="off"
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            {...otherProps}
          />
        )}
        {showAmountOrder && (
          <AmountOrder
            style={{ marginTop: localPx2rem(view ? 0 : 4) }}
            value={(view && rateChangeAvailable)
              ? new Decimal(state?.value || 0).mul(exchangeRate).toNumber()
              : state?.value}
          />
        )}
      </div>
      {!!showCurrency && (
        <div
          className={cx('currency-type-right', {
            view: view,
          })}
        >
          {view ? (
            <div className={cx('currency-type-text')} style={currencyStyle}>
              {!isBlank(state?.value) && state?.currency ? (
                <span className={cx('currency-type-value')}>
                  {
                    currencyEnum[
                      viewCurrencyType && rateChangeAvailable
                        ? viewCurrencyType.toLowerCase()
                        : state?.currency?.toLocaleLowerCase?.()
                    ]
                  }
                </span>
              ) : (
                '--'
              )}
            </div>
          ) : (
            <Select
              style={{ ...stylePx2Rem(selectStyle) }}
              value={state?.currency?.toLocaleLowerCase()}
              onChange={handleSelectChange}
              options={currencyOptions}
              disabled={selectDisabled || disabled}
              {...otherProps}
              zIndex={10000}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CurrencyType;
